import { useCallback, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { CellValueChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { LoaderCircleIcon } from 'lucide-react';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { type Control, type FieldErrors, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetBreedsQuery } from '@/app/services/lookups';
import { useDefaultGridOptions } from '@/grid';
import { cn } from '@/lib/utils';
import {
  type BulkDonorValidationResponse,
  useUpsertBulkDonors,
  useValidateBulkDonors,
} from '@/services/bulk-donor';
import {
  type BulkDonorScheduleValidationResponse,
  useUpsertBulkDonorSchedules,
  useValidateBulkDonorSchedules,
  ValidationMessageLevel,
} from '@/services/bulk-donor-scheduling';

import { AspirationDate } from './components/aspiration-date';
import { AspirationDropdown } from './components/aspiration-dropdown';
import { CellRenderer } from './components/cell-renderer';
import {
  type DonorMismatchData,
  DonorMismatchDialog,
} from './components/donor-mismatch-dialog';
import { FertilizationDropdown } from './components/fertilization-dropdown';
import { ViewSwitcher } from './components/view-switcher';
import {
  Messages,
  useBulkDonorSchedulingStore,
} from './hooks/use-bulk-donor-scheduling-store';
import { useColumns, useDonorColumns } from './hooks/use-columns';
import { useNotifications } from './hooks/use-notifications';
import { parseExcelFile } from './utils/excel-parser';
import {
  BulkDonorFieldsInput,
  BulkDonorFieldsSchema,
  BulkDonorInput,
  BulkDonorRowsSchema,
  type BulkDonorScheduleFieldsInput,
  BulkDonorScheduleFieldsSchema,
  type BulkDonorScheduleInput,
  BulkDonorScheduleRowsSchema,
} from './utils/schema';

import './index.css';

const normalizeBreedValue = (value: string) => value.trim().toLowerCase();

export function BulkDonorSchedulingPage() {
  const defaultGridOptions = useDefaultGridOptions();
  const { t } = useTranslation();

  const [sheetState, dispatch] = useBulkDonorSchedulingStore();
  const { data: bovineBreeds } = useGetBreedsQuery({});

  const donorForm = useForm<BulkDonorFieldsInput>({
    resolver: zodResolver(BulkDonorFieldsSchema),
  });

  const donorScheduleForm = useForm<BulkDonorScheduleFieldsInput>({
    resolver: zodResolver(BulkDonorScheduleFieldsSchema),
  });

  const currentForm =
    sheetState.viewType === 'donor' ? donorForm : donorScheduleForm;
  const { control, handleSubmit, reset, getValues } = currentForm;

  const grid = useRef<AgGridReact<BulkDonorScheduleInput>>(null);
  const api = grid.current?.api;

  const [isGridEditing, setGridEditing] = useState(false);
  const [mismatchDialog, setMismatchDialog] = useState<{
    visible: boolean;
    rowIndex: number | null;
    donorName: string;
    mismatchData: DonorMismatchData | null;
  }>({
    visible: false,
    rowIndex: null,
    donorName: '',
    mismatchData: null,
  });

  const notifications = useNotifications();
  const validBovineBreedValues = useMemo(() => {
    const values = new Set<string>();
    for (const breed of bovineBreeds || []) {
      if (breed?.code) {
        values.add(normalizeBreedValue(breed.code));
      }
      if (breed?.name) {
        values.add(normalizeBreedValue(breed.name));
      }
    }
    return values;
  }, [bovineBreeds]);

  const donorColumns = useDonorColumns({
    readonly: sheetState.readonly,
  });
  const donorScheduleColumns = useColumns({
    readonly: sheetState.readonly,
    fertilizationSiteId: currentForm.watch().fertilizationSiteId,
  });

  const columns =
    sheetState.viewType === 'donor' ? donorColumns : donorScheduleColumns;

  const validateDonorSchedule = useValidateBulkDonorSchedules();
  const validateDonor = useValidateBulkDonors();
  const upsertSchedulesMutation = useUpsertBulkDonorSchedules();
  const upsertDonorsMutation = useUpsertBulkDonors();

  const handleValidate = useCallback(
    async (fields: BulkDonorScheduleFieldsInput | BulkDonorFieldsInput) => {
      if (!api) {
        return;
      }

      dispatch({ type: 'client_validating' });

      api.stopEditing();

      const rows = sheetState.rows.map((row) => {
        if (!row || Object.values(row).filter((v) => !!v).length === 0) {
          return undefined;
        }
        return row;
      });

      const rowsResult = (
        sheetState.viewType === 'donor'
          ? BulkDonorRowsSchema
          : BulkDonorScheduleRowsSchema
      ).safeParse(rows);

      const inputs = rowsResult.data?.filter(
        (row): row is BulkDonorScheduleInput | BulkDonorInput =>
          row !== undefined,
      );

      if (!rowsResult.success || !inputs?.length) {
        const errors: Record<string, string> = {};

        const colId = api
          .getColumns()
          ?.find((c) => c.getColDef().field)
          ?.getColDef().field;
        if (!inputs?.length && colId) {
          errors[`${0}.${colId}`] = 'At least one valid row is required';
        }

        if (rowsResult.error?.issues) {
          for (const issue of rowsResult.error.issues) {
            const rowIndex = issue.path[0] as string;
            const colId = issue.path[1] as string;
            errors[`${rowIndex}.${colId}`] = issue.message;
          }
        }

        dispatch({ type: 'client_validated', errors });

        notifications.validationErrors();

        return;
      }

      if (bovineBreeds?.length) {
        const breedErrors: Record<string, string> = {};
        rowsResult.data.forEach((row, rowIndex) => {
          if (!row) {
            return;
          }

          const donorBreed = row.donorBreed?.trim();
          if (
            donorBreed &&
            !validBovineBreedValues.has(normalizeBreedValue(donorBreed))
          ) {
            breedErrors[`${rowIndex}.donorBreed`] =
              'Breed must be a valid bovine breed code or name';
          }
        });

        if (Object.keys(breedErrors).length > 0) {
          dispatch({ type: 'client_validated', errors: breedErrors });
          notifications.validationErrors();
          return;
        }
      }

      dispatch({ type: 'client_validated', errors: undefined });

      try {
        const variables: any = {
          ...fields,
          type: sheetState.viewType,
          inputs: rowsResult.data as BulkDonorScheduleInput[],
        };

        if (sheetState.viewType === 'donor-schedule') {
          const scheduleFields = fields as BulkDonorScheduleFieldsInput;
          const date = scheduleFields.aspirationDate;
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          variables.aspirationDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        } else {
          const donorFields = fields as BulkDonorFieldsInput;
          if (donorFields.aspirationDate) {
            const date = donorFields.aspirationDate;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            variables.aspirationDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
          }
        }

        const result:
          | BulkDonorScheduleValidationResponse
          | BulkDonorValidationResponse = await (
          sheetState.viewType === 'donor'
            ? validateDonor
            : validateDonorSchedule
        ).mutateAsync(variables);

        let messages: Messages | undefined = undefined;
        let errors: Record<string, string> | undefined = undefined;

        for (const validation of result.fields) {
          const field = validation.field;
          if (!messages) {
            messages = {};
          }
          messages[field] = {
            level: ValidationMessageLevel.MESSAGE,
            message: validation.message,
          };
        }

        let hasMismatch = false;
        for (const validation of result.inputs) {
          const rowIndex = validation.index.toString();
          const colId = validation.field;

          if (
            validation.correctedValue &&
            validation.level === ValidationMessageLevel.MESSAGE
          ) {
            const updatedRow = {
              ...sheetState.rows[validation.index],
              [colId]: validation.correctedValue,
            };
            dispatch({
              type: 'set_row',
              rowIndex: validation.index,
              data: updatedRow,
            });
          }

          if (validation.level === ValidationMessageLevel.ERROR) {
            if (!errors) {
              errors = {};
            }
            errors[`${rowIndex}.${colId}`] = validation.message;
          } else {
            if (!messages) {
              messages = {};
            }
            messages[`${rowIndex}.${colId}`] = {
              level: validation.level,
              message: validation.message,
              suggestions: validation.suggestions || undefined,
            };

            if (
              validation.level === ValidationMessageLevel.WARNING &&
              validation.mismatchData &&
              !hasMismatch
            ) {
              hasMismatch = true;
              const row = sheetState.rows[validation.index];
              setMismatchDialog({
                visible: true,
                rowIndex: validation.index,
                donorName: row?.donorNameToMatch || '',
                mismatchData: validation.mismatchData,
              });
            }
          }
        }

        if (errors) {
          notifications.validationErrors();
        } else if (!hasMismatch) {
          notifications.validationSuccessful();
        }

        dispatch({ type: 'server_validated', errors, messages });
      } catch (error) {
        notifications.validationServerError();

        dispatch({ type: 'server_error' });
        console.error('Error while validating data', error);
      }
    },
    [
      api,
      dispatch,
      notifications,
      sheetState.rows,
      validateDonor,
      validateDonorSchedule,
      sheetState.viewType,
      bovineBreeds,
      validBovineBreedValues,
    ],
  );
  const handleInvalidSubmit = useCallback(
    (
      _errors: FieldErrors<BulkDonorScheduleFieldsInput | BulkDonorFieldsInput>,
    ) => {
      notifications.validationErrors();
    },
    [notifications],
  );

  const handleUpload = useCallback(async () => {
    if (!api) {
      return;
    }

    dispatch({ type: 'uploading' });

    try {
      const fields = getValues();
      const rows = (
        sheetState.viewType === 'donor'
          ? BulkDonorRowsSchema
          : BulkDonorScheduleRowsSchema
      )
        .parse(
          sheetState.rows.filter((row) => {
            return !!row && !!Object.values(row).filter((v) => !!v).length;
          }),
        )
        .filter(
          (row): row is BulkDonorScheduleInput | BulkDonorInput =>
            row !== undefined,
        );

      if (sheetState.viewType === 'donor') {
        await upsertDonorsMutation.mutateAsync(rows);
      } else {
        const variables: any = {
          ...fields,
          inputs: rows,
        };

        if (sheetState.viewType === 'donor-schedule') {
          const scheduleFields = fields as BulkDonorScheduleFieldsInput;
          const date = scheduleFields.aspirationDate;
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          variables.aspirationDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        } else {
          const donorFields = fields as BulkDonorFieldsInput;
          if (donorFields.aspirationDate) {
            const date = donorFields.aspirationDate;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            variables.aspirationDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
          }
        }

        await upsertSchedulesMutation.mutateAsync(variables);
      }

      notifications.uploadSuccessful();

      dispatch({ type: 'success' });
    } catch (error) {
      notifications.uploadServerError();

      dispatch({ type: 'server_error' });
      console.error('Error while uploading data', error);
    }
  }, [
    api,
    dispatch,
    getValues,
    notifications,
    sheetState.rows,
    upsertSchedulesMutation,
    upsertDonorsMutation,
    sheetState.viewType,
  ]);

  const handleCellValueChanged = useCallback(
    (e: CellValueChangedEvent<BulkDonorScheduleInput>) => {
      const rowIndex = e.rowIndex;
      const data = e.data;
      if (rowIndex === null || data === undefined) {
        return;
      }

      const focusedCell = e.api.getFocusedCell();

      dispatch({ type: 'set_row', rowIndex, data });

      if (focusedCell) {
        e.api.stopEditing();

        // dispatch "set_row" causes a re-render and the grid loses focus
        // the timeout is necessary to wait for the re-render to complete
        setTimeout(() => {
          e.api.setFocusedCell(
            focusedCell.rowIndex,
            focusedCell.column,
            focusedCell.rowPinned,
          );
          e.api.startEditingCell({
            rowIndex: focusedCell.rowIndex,
            colKey: focusedCell.column,
            rowPinned: focusedCell.rowPinned,
          });
        }, 300);
      }
    },
    [dispatch],
  );

  const handleViewChange = useCallback(
    (viewType: 'donor' | 'donor-schedule') => {
      dispatch({ type: 'switch_view', viewType });
    },
    [dispatch],
  );

  const handleUseExistingData = useCallback(() => {
    if (mismatchDialog.rowIndex === null || !mismatchDialog.mismatchData) {
      return;
    }

    const rowIndex = mismatchDialog.rowIndex;
    const currentRow = sheetState.rows[rowIndex];
    if (!currentRow) {
      return;
    }

    const updatedRow: BulkDonorScheduleInput = {
      ...currentRow,
      donorRegistrationNumber:
        mismatchDialog.mismatchData.existingRegistrationNumber ||
        currentRow.donorRegistrationNumber,
      pedigreeName:
        mismatchDialog.mismatchData.existingPedigreeName ||
        currentRow.pedigreeName,
      birthDate:
        mismatchDialog.mismatchData.existingBirthDate || currentRow.birthDate,
    };

    dispatch({ type: 'set_row', rowIndex, data: updatedRow });

    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });

    notifications.validationSuccessful();
  }, [mismatchDialog, sheetState.rows, dispatch, notifications]);

  const handleCreateNewDonor = useCallback(() => {
    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });

    notifications.validationSuccessful();
  }, [notifications]);

  const handleCancelMismatch = useCallback(() => {
    setMismatchDialog({
      visible: false,
      rowIndex: null,
      donorName: '',
      mismatchData: null,
    });
  }, []);
  const processFileUpload = useCallback(
    async (file: File) => {
      try {
        const data = await parseExcelFile(file);

        if (data.length === 0) {
          notifications.fileProcessingError(
            'No valid data found in Excel file',
          );
          return;
        }

        dispatch({ type: 'upload_from_excel', data });
        notifications.fileUploadSuccess(
          `Successfully loaded ${data.length} rows`,
        );
      } catch (error) {
        console.error('Error processing Excel file:', error);
        notifications.fileProcessingError(
          error instanceof Error
            ? error.message
            : 'Failed to process Excel file',
        );
      }
    },
    [dispatch, notifications],
  );

  const showFilePicker = useCallback(() => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.xlsx';
    fileInput.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        await processFileUpload(file);
      }
    };
    fileInput.click();
  }, [processFileUpload]);

  const handleUploadClick = useCallback(() => {
    const hasData = sheetState.rows.some(
      (row) =>
        row &&
        Object.values(row).some((val) => val !== undefined && val !== ''),
    );

    if (!hasData) {
      showFilePicker();
      return;
    }

    confirmDialog({
      message: t('bulkDonorScheduling.uploadConfirmMessage'),
      header: t('bulkDonorScheduling.uploadConfirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      accept: showFilePicker,
      acceptLabel: t('common.yes'),
      rejectLabel: t('common.no'),
    });
  }, [sheetState.rows, showFilePicker, t]);

  return (
    <div className="h-[calc(100%-4rem)]">
      <ConfirmDialog />

      <DonorMismatchDialog
        visible={mismatchDialog.visible}
        mismatchData={mismatchDialog.mismatchData}
        donorName={mismatchDialog.donorName}
        onUseExisting={handleUseExistingData}
        onCreateNew={handleCreateNewDonor}
        onCancel={handleCancelMismatch}
      />

      <form
        onSubmit={handleSubmit(handleValidate, handleInvalidSubmit)}
        className="mb-4 flex flex-col justify-between gap-4 lg:flex-row"
      >
        <div className="flex flex-col gap-2 md:flex-row">
          <ViewSwitcher
            className="flex-grow self-start"
            currentView={sheetState.viewType}
            onViewChange={handleViewChange}
            disabled={sheetState.loading || isGridEditing}
          />

          {sheetState.viewType === 'donor-schedule' && (
            <>
              <AspirationDate
                className="flex-grow"
                control={control as Control<any>}
                disabled={sheetState.readonly || sheetState.loading}
                valueChanged={() => {
                  dispatch({ type: 'idle' });
                }}
              />
              <AspirationDropdown
                className="flex-grow"
                control={control as Control<any>}
                disabled={sheetState.readonly || sheetState.loading}
                valueChanged={() => {
                  dispatch({ type: 'idle' });
                }}
              />
              <FertilizationDropdown
                className="flex-grow"
                control={control as Control<any>}
                disabled={sheetState.readonly || sheetState.loading}
                valueChanged={() => {
                  dispatch({ type: 'idle' });
                }}
              />
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {sheetState.type === 'idle' &&
            sheetState.viewType === 'donor-schedule' && (
              <Button
                type="button"
                className="justify-center"
                disabled={sheetState.loading || isGridEditing}
                onClick={handleUploadClick}
              >
                {t('bulkDonorScheduling.Upload')}
              </Button>
            )}
          {sheetState.type === 'server_validated' ||
          sheetState.type === 'uploading' ||
          sheetState.type === 'success' ? (
            <Button
              severity="success"
              className="flex-grow justify-center"
              disabled={
                sheetState.loading ||
                sheetState.type === 'success' ||
                isGridEditing
              }
              onClick={handleUpload}
            >
              {sheetState.loading ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {t('bulkDonorScheduling.Uploading')}
                </>
              ) : (
                t('bulkDonorScheduling.Upload')
              )}
            </Button>
          ) : (
            <Button
              className="justify-center"
              disabled={sheetState.loading || isGridEditing}
              type="submit"
            >
              {sheetState.loading ? (
                <>
                  <LoaderCircleIcon className="animate-spin" />
                  {t('bulkDonorScheduling.Validating')}
                </>
              ) : (
                t('bulkDonorScheduling.Validate')
              )}
            </Button>
          )}

          <Button
            className="justify-center"
            disabled={
              sheetState.loading ||
              sheetState.type === 'success' ||
              isGridEditing
            }
            onClick={() => {
              dispatch({ type: 'add_rows', count: 10 });
            }}
          >
            {t('bulkDonorScheduling.Add_Rows')}
          </Button>

          <Button
            className="justify-center"
            size="small"
            severity="danger"
            disabled={sheetState.loading || isGridEditing}
            onClick={() => {
              reset();
              dispatch({ type: 'reset' });
            }}
          >
            {t('bulkDonorScheduling.Reset')}
          </Button>
        </div>
      </form>

      <div className="h-full w-full overflow-auto">
        <AgGridReact
          gridOptions={defaultGridOptions}
          ref={grid}
          className={cn(
            'ag-theme-transova spreadsheet-grid h-full',
            sheetState.readonly && 'spreadsheet-grid--readonly',
            sheetState.type === 'success' && 'spreadsheet-grid--success',
          )}
          suppressCsvExport
          suppressExcelExport
          suppressHorizontalScroll={false}
          tooltipInteraction
          tooltipShowDelay={0}
          context={sheetState}
          defaultColDef={{
            cellRenderer: CellRenderer,
            resizable: true,
            suppressHeaderMenuButton: true,
            tooltipValueGetter(params) {
              const rowIndex = params.rowIndex;
              const fieldName =
                params.colDef &&
                'field' in params.colDef &&
                (params.colDef.field as string);
              if (rowIndex === undefined || !fieldName) {
                return;
              }

              const error = sheetState.errors?.[`${rowIndex}.${fieldName}`];
              const message =
                'messages' in sheetState &&
                sheetState.messages?.[`${rowIndex}.${fieldName}`]?.message;

              return error || message;
            },
            cellClass: (params) => {
              const rowIndex = params.rowIndex;
              const fieldName =
                params.colDef &&
                'field' in params.colDef &&
                params.colDef.field;
              if (!fieldName) {
                return;
              }
              const hasMessage =
                'messages' in sheetState &&
                sheetState.messages?.[`${rowIndex}.${fieldName}`]?.level ===
                  ValidationMessageLevel.MESSAGE;
              const hasWarning =
                'messages' in sheetState &&
                sheetState.messages?.[`${rowIndex}.${fieldName}`]?.level ===
                  ValidationMessageLevel.WARNING;
              const hasError =
                !!sheetState.errors?.[`${rowIndex}.${fieldName}`];

              return cn(
                hasMessage && '--has-message',
                hasWarning && '--has-warning',
                hasError && '--has-error',
              );
            },
          }}
          columnDefs={columns}
          onCellValueChanged={handleCellValueChanged}
          rowData={sheetState.rows}
          enableRangeSelection
          enableFillHandle
          autoSizePadding={2}
          stopEditingWhenCellsLoseFocus
          onCellEditingStarted={() => setGridEditing(true)}
          onCellEditingStopped={() => setGridEditing(false)}
        />
      </div>
    </div>
  );
}
